import { TFunction } from "i18next";
import { List } from "immutable";
import { WarehouseBranchMap } from "../../types/api/warehouse";

export type ParcelsNavItem = {
  to: string;
  label: string;
  end?: boolean;
};

/**
 * Which branch field labels the branch links. "sb_name" falls back to "name"
 * when a branch has no short name.
 */
export type BranchLabelField = "name" | "sb_name";

export type ParcelsNavOptions = {
  /**
   * Containers are a service of their own — only container clients get the
   * destination (see `authSelectCanUseContainers`). Required so a new consumer
   * has to state the capability rather than silently show or hide the link.
   */
  canUseContainers: boolean;
  branchLabelField?: BranchLabelField;
  /** Put the warehouse branches ahead of "expected" instead of after it. */
  branchesFirst?: boolean;
};

const getBranchLabel = (
  branch: WarehouseBranchMap,
  field: BranchLabelField,
): string =>
  field === "sb_name"
    ? branch.get("sb_name") ?? branch.get("name")
    : branch.get("name");

/**
 * The parcels sub-navigation destinations, in render order. Shared by the
 * ParcelHeader tabs and the LeftMenu parcels sub-links so a destination is
 * added (or gated) in one place.
 */
export const buildParcelsNav = (
  warehouseBranches: List<WarehouseBranchMap>,
  t: TFunction,
  {
    canUseContainers,
    branchLabelField = "name",
    branchesFirst = false,
  }: ParcelsNavOptions,
): ParcelsNavItem[] => {
  const branches: ParcelsNavItem[] = warehouseBranches
    .map((branch) => ({
      to: `/parcels/${branch.get("id")}`,
      label: getBranchLabel(branch, branchLabelField),
      end: true,
    }))
    .toArray();

  const expected: ParcelsNavItem = {
    to: "/parcels/expected",
    label: t("pages.expected"),
  };

  return [
    ...(branchesFirst ? [...branches, expected] : [expected, ...branches]),
    { to: "/parcels/outgoing", label: t("pages.outgoing") },
    ...(canUseContainers
      ? [{ to: "/parcels/containers", label: t("pages.containers") }]
      : []),
    { to: "/parcels/history", label: t("pages.history") },
  ];
};
